import React, { useCallback, useState } from 'react'
import { Box, Card, Grid, TextField, Typography } from '@mui/material'
import { DateDayControl } from 'src/admin/core/components/date/DateDayControl'
import { ProductOnlineCheckSelect, SearchButton, LocationLineCheckSelect } from 'src/admin/core'
import { ProductOnlineCheckOption } from 'src/admin/core/components/Select/ProductOnlineCheckSelect'
import { LocationLineCheckOption } from 'src/admin/core/components/Select/LocationLineCheckSelect'
import { useAppTranslation } from 'src/admin/core/hooks'
import { formatJSDateToLocal } from 'src/admin/core/services/dateFunctions'
import { useFinancialFlows } from '../context'
import { FilterModel } from '../context/types'
import { getAppBarState } from 'src/store'

export type { FilterModel } from '../context/types'

interface FiltersProps {
  onSearch?: (filters: FilterModel) => void
  isLoading?: boolean
}

export const Filters: React.FC<FiltersProps> = ({ onSearch, isLoading = false }) => {
  const { label } = useAppTranslation()
  const { filters, setFilters, selectedReportType } = useFinancialFlows()
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({})
  const { userEnterprise } = getAppBarState().appState

  // Determine which filters to show based on selectedReportType
  const showAgencyDropdown = selectedReportType?.AgencyRequired ?? false
  const showAgencyTextField = selectedReportType?.AgencyFilter ?? false
  const showProductDropdown = selectedReportType?.ProductRequired ?? false
  const showProductTextField = selectedReportType?.ProductFilter ?? false

  const handleFromDateChange = (date: Date) => {
    setFilters({
      ...filters,
      from: formatJSDateToLocal(date) + 'T00:00:00Z',
    })
    setValidationErrors((prev) => ({ ...prev, from: false }))
  }

  const handleToDateChange = (date: Date) => {
    setFilters({
      ...filters,
      to: formatJSDateToLocal(date) + 'T23:59:59Z',
    })
    setValidationErrors((prev) => ({ ...prev, to: false }))
  }

  const handleAgencyFilterTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({
      ...filters,
      agencyFilterText: event.target.value,
    })
  }

  const handleProductFilterTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({
      ...filters,
      productFilterText: event.target.value,
    })
  }

  const handleSearch = useCallback(() => {
    // Build required fields based on selected report type
    const errors: Record<string, boolean> = {}

    // Date fields are always required
    errors.from = !filters.from || filters.from.trim() === ''
    errors.to = !filters.to || filters.to.trim() === ''

    // Dropdowns are NOT required - if not selected, entity_id will be 0

    setValidationErrors(errors)

    const hasErrors = Object.values(errors).some((error) => error)
    if (hasErrors) {
      return
    }

    // Clear validation errors on successful validation
    setValidationErrors({})

    // Prepare filters with default values for dropdowns if not selected
    const searchFilters: FilterModel = {
      ...filters,
      product: filters.product || '0',
      agencyId: filters.agencyId || '0',
    }

    // Trigger search callback
    onSearch?.(searchFilters)
  }, [filters, onSearch])

  // Calculate distinct visible inputs count
  const inputsCount = 2 // From + To
    + (showProductDropdown ? 1 : 0)
    + (showProductTextField ? 1 : 0)
    + (showAgencyDropdown ? 1 : 0)
    + (showAgencyTextField ? 1 : 0)

  return (
    <Card sx={{ p: 1 }}>
      <Box p={1}>
        <Grid container spacing={2}>
          {/* Date From - Always visible */}
          <Grid item xs={6} md={2} lg={2}>
            <DateDayControl 
              onChange={handleFromDateChange} 
              showButtons={false} 
              label={label('DESDE')} 
            />
            {validationErrors.from && (
              <Typography variant="body1" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {label('REQUERIDO')}
              </Typography>
            )}
          </Grid>

          {/* Date To - Always visible */}
          <Grid item xs={6} md={2} lg={2}>
            <DateDayControl 
              onChange={handleToDateChange} 
              showButtons={false} 
              label={label('HASTA')} 
            />
            {validationErrors.to && (
              <Typography variant="body1" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {label('REQUERIDO')}
              </Typography>
            )}
          </Grid>

          {/* Product Dropdown - Only when ProductRequired is true */}
          {showProductDropdown && (
            <Grid item xs={6} md={2} lg={2}>
              <ProductOnlineCheckSelect
                disableClearable={false}
                selectDefaultProduct={false}
                disabled={false}
                value={filters.productObj as ProductOnlineCheckOption | null}
                onChange={(val: ProductOnlineCheckOption | null) => {
                  setFilters({
                    ...filters,
                    product: val ? String(val.value) : '',
                    productObj: val || undefined,
                  })
                  setValidationErrors((prev) => ({ ...prev, product: false }))
                }}
                getDefaultValue={(val: ProductOnlineCheckOption | null) => {
                  setFilters({
                    ...filters,
                    product: val ? String(val.value) : '',
                    productObj: val || undefined,
                  })
                  setValidationErrors((prev) => ({ ...prev, product: false }))
                }}
              />
              {validationErrors.product && (
                <Typography variant="body1" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {label('REQUERIDO')}
                </Typography>
              )}
            </Grid>
          )}

          {/* Product Text Filter - Only when ProductFilter is true */}
          {showProductTextField && (
            <Grid item xs={6} md={2} lg={2}>
              <TextField
                fullWidth
                label={label('FILTRAR_PRODUCTO')}
                placeholder={label('NOMBRE_PRODUCTO')}
                value={filters.productFilterText}
                onChange={handleProductFilterTextChange}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          )}

          {/* Agency Dropdown - Only when AgencyRequired is true */}
          {showAgencyDropdown && (
            <Grid item xs={6} md={2} lg={2}>
              <LocationLineCheckSelect
                disableClearable={false}
                selectDefaultLocation={false}
                disabled={false}
                value={filters.locationLineObj as LocationLineCheckOption | null}
                onChange={(val: LocationLineCheckOption | null) => {
                  setFilters({
                    ...filters,
                    agencyId: val ? String(val.value) : '',
                    locationLineObj: val || undefined,
                  })
                  setValidationErrors((prev) => ({ ...prev, agencyId: false }))
                }}
                getDefaultValue={(val: LocationLineCheckOption | null) => {
                  setFilters({
                    ...filters,
                    agencyId: val ? String(val.value) : '',
                    locationLineObj: val || undefined,
                  })
                  setValidationErrors((prev) => ({ ...prev, agencyId: false }))
                }}
              />
              {validationErrors.agencyId && (
                <Typography variant="body1" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {label('REQUERIDO')}
                </Typography>
              )}
            </Grid>
          )}

          {/* Agency Text Filter - Only when AgencyFilter is true */}
          {showAgencyTextField && (
            <Grid item xs={6} md={2} lg={2}>
              <TextField
                fullWidth
                label={label('FILTRAR_AGENCIA')}
                placeholder={label('NOMBRE_AGENCIA')}
                value={filters.agencyFilterText}
                onChange={handleAgencyFilterTextChange}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          )}

          {/* Search Button - Always visible */}
          <Grid item xs={inputsCount % 2 === 0 ? 12 : 6} md={2} lg={2}>
            <SearchButton
              color="primary"
              title={label('CONSULTAR')}
              onClick={handleSearch}
              isLoading={isLoading}
              style={{ height: '56px', width: '100%' }}
            />
          </Grid>
        </Grid>
      </Box>
    </Card>
  )
}

export default Filters
